// Multi-session chat application.
// Session data flows through SessionContext; this component manages the
// active streaming state and syncs completed messages back to the context.
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageList, type MessageData } from './MessageList';
import { ChatInput, type ChatInputHandle } from './ChatInput';
import ChatTopBar from './ChatTopBar';
import { useLlmStatus } from './useLlmStatus';
import { streamChat, type StreamChatBody } from '../../lib/sse';
import { useSessionContext } from '../../contexts/SessionContext';
import { useGraphStorageContext } from '../../contexts/GraphStorageContext';
import { deriveTitle } from '../../lib/sessions';
import { emptyAccumulatedGraph, mergeGraphEvent, clearUnpinned, mergeNeighborExpansion, deriveContextFromGraph } from '../../lib/graphMerge';
import { AccumulatedSubgraphPanel } from '../graph/AccumulatedSubgraphPanel';
import type {
  ChatMessage,
  PipelineStep,
  PlanningEvent,
  RoutingEvent,
  StepDetail,
  SourceDoc,
  GraphTraversalEvent,
  ProfileEvent,
  ScoreThreshold,
  DetectedIOC,
  ChatOptions,
  StoredMessage,
  AccumulatedGraph,
} from '../../types';
import { DEFAULT_CHAT_OPTIONS, normalizeChatMode } from '../../types';

const OPTIONS_KEY = 'malpedia-chat-options';

let msgIdCounter = 0;
const newId = () => `${Date.now()}-${++msgIdCounter}`;

function loadOptions(): ChatOptions {
  try {
    const raw = localStorage.getItem(OPTIONS_KEY);
    if (!raw) return { ...DEFAULT_CHAT_OPTIONS };
    const opts: ChatOptions = { ...DEFAULT_CHAT_OPTIONS, ...JSON.parse(raw) };
    return { ...opts, mode: normalizeChatMode(opts.mode) };
  } catch {
    return { ...DEFAULT_CHAT_OPTIONS };
  }
}

function persistOptions(opts: ChatOptions) {
  try {
    localStorage.setItem(OPTIONS_KEY, JSON.stringify(opts));
  } catch {
    /* ignore */
  }
}

/** Convert StoredMessage[] from a session into MessageData[] for rendering. */
function storedToMessageData(stored: StoredMessage[]): MessageData[] {
  return stored.map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    sources: m.sources,
    completedSteps: m.completedSteps,
    stepDetails: m.stepDetails,
    planningEvent: m.planningEvent ?? null,
    routingEvent: m.routingEvent ?? null,
    graphTraversalEvent: m.graphTraversalEvent ?? null,
    profileEvent: m.profileEvent ?? null,
    scoreThreshold: m.scoreThreshold ?? null,
    queryDetectedIocs: m.queryDetectedIocs,
    responseDetectedIocs: m.responseDetectedIocs,
    turnId: m.turnId,
  }));
}

/** Convert MessageData[] to StoredMessage[] for persistence. */
function messageDataToStored(messages: MessageData[]): StoredMessage[] {
  return messages
    .filter((m) => !m.streaming)
    .map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      sources: m.sources,
      completedSteps: m.completedSteps,
      stepDetails: m.stepDetails,
      planningEvent: m.planningEvent ?? null,
      routingEvent: m.routingEvent ?? null,
      graphTraversalEvent: m.graphTraversalEvent ?? null,
      profileEvent: m.profileEvent ?? null,
      scoreThreshold: m.scoreThreshold ?? null,
      queryDetectedIocs: m.queryDetectedIocs,
      responseDetectedIocs: m.responseDetectedIocs,
      turnId: m.turnId,
    }));
}

export default function ChatApp() {
  const ctx = useSessionContext();
  const { activeSession, activeSessionId } = ctx;
  const navigate = useNavigate();
  const { saveGraph } = useGraphStorageContext();

  const [messages, setMessages] = useState<MessageData[]>(() =>
    activeSession ? storedToMessageData(activeSession.messages) : [],
  );
  const [streaming, setStreaming] = useState(false);
  const [useRag, setUseRag] = useState(true);
  const [chatOptions, setChatOptions] = useState<ChatOptions>(() => loadOptions());
  const [accGraph, setAccGraph] = useState<AccumulatedGraph>(() =>
    activeSession?.accumulatedGraph ?? emptyAccumulatedGraph(),
  );
  const [subgraphScope, setSubgraphScope] = useState<'none' | 'search_within' | 'expand_from'>('none');
  const [graphFullscreen, setGraphFullscreen] = useState(false);
  const [hasUnseenGraphData, setHasUnseenGraphData] = useState(false);
  const { stats, llmStatus, llmOk } = useLlmStatus();

  const abortCtrlRef = useRef<AbortController | null>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const accGraphRef = useRef(accGraph);
  accGraphRef.current = accGraph;
  const chatInputRef = useRef<ChatInputHandle>(null);

  // Track the session id we are currently viewing so the switch effect
  // can detect changes, and which id we've already populated state for
  // (server-backed sessions hydrate async via useChat; we need to fill
  // state when the detail resolves, not on switch alone).
  const prevSessionIdRef = useRef(activeSessionId);
  const initializedForIdRef = useRef<string | null>(activeSession ? activeSessionId : null);

  // Reset local state on session switch — clears the previous session's
  // messages/graph and aborts any in-flight stream. Runs before the
  // hydration effect so we don't briefly show the previous chat's data.
  useEffect(() => {
    if (activeSessionId !== prevSessionIdRef.current) {
      prevSessionIdRef.current = activeSessionId;
      abortCtrlRef.current?.abort();
      abortCtrlRef.current = null;
      setStreaming(false);
      setMessages([]);
      setAccGraph(emptyAccumulatedGraph());
      initializedForIdRef.current = null;
    }
  }, [activeSessionId]);

  // Hydrate local state from activeSession when it resolves for the
  // current id. Skipped during streaming so in-flight messages aren't
  // wiped by a mid-stream useChat refetch (e.g. from notifyTurnComplete
  // on a backgrounded sibling stream). Idempotent: keyed by id so a
  // re-render with the same activeSession is a no-op.
  useEffect(() => {
    if (streaming) return;
    if (!activeSession || !activeSessionId) return;
    if (activeSession.id !== activeSessionId) return;
    if (initializedForIdRef.current === activeSessionId) return;
    setMessages(storedToMessageData(activeSession.messages));
    setAccGraph(activeSession.accumulatedGraph ?? emptyAccumulatedGraph());
    initializedForIdRef.current = activeSessionId;
  }, [activeSessionId, activeSession, streaming]);

  useEffect(() => {
    persistOptions(chatOptions);
  }, [chatOptions]);

  // Abort any in-flight stream on unmount.
  useEffect(() => {
    return () => {
      abortCtrlRef.current?.abort();
    };
  }, []);

  // Pick up pending graph context from the graph explorer
  useEffect(() => {
    const pending = sessionStorage.getItem('malpedia-pending-graph-context');
    if (pending) {
      sessionStorage.removeItem('malpedia-pending-graph-context');
      chatInputRef.current?.setValue(pending);
    }
  }, []);

  const estimatedTokens = useMemo(
    () =>
      messages
        .filter((m) => !m.streaming)
        .reduce((sum, m) => sum + Math.ceil(m.content.length / 4), 0),
    [messages],
  );

  const buildHistory = (msgs: MessageData[]): ChatMessage[] =>
    msgs
      .filter((m) => !m.streaming)
      .map((m) => ({ role: m.role, content: m.content }));

  const handleChatOptionsChange = useCallback((partial: Partial<ChatOptions>) => {
    setChatOptions((prev) => ({ ...prev, ...partial }));
  }, []);

  const handleSend = useCallback(
    async (text: string) => {
      if (streaming) return;

      // Capture session identity at send time so that completion writes
      // go to the correct session even if the user switches mid-stream.
      const startSessionId = activeSessionId;
      const startSessionTitle = activeSession?.title;

      const effectiveMode = useRag ? chatOptions.mode : 'llm_only';

      const userMsg: MessageData = { id: newId(), role: 'user', content: text };
      const assistantId = newId();
      const assistantMsg: MessageData = {
        id: assistantId,
        role: 'assistant',
        content: '',
        streaming: true,
        completedSteps: [],
      };

      const baselineMessages = messagesRef.current;
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setStreaming(true);

      const history = buildHistory(baselineMessages);

      const isNamedProfile = chatOptions.retrievalProfile !== 'auto' && chatOptions.retrievalProfile !== 'manual';
      const isManual = chatOptions.retrievalProfile === 'manual';

      const body: StreamChatBody = {
        message: text,
        history,
        chat_id: activeSession?.serverChatId,
        is_temporary: activeSession?.temporary || undefined,
        mode: effectiveMode === 'auto' ? undefined : effectiveMode,
        ...(isNamedProfile
          ? { retrieval_depth: chatOptions.retrievalProfile }
          : { top_k: chatOptions.topK }),
        ...(isManual && {
          custom_top_sources: chatOptions.customTopSources,
          custom_fusion_cap: chatOptions.customFusionCap,
          custom_max_chunks_per_doc: chatOptions.customMaxChunksPerDoc || undefined,
        }),
        ioc_pivot: chatOptions.iocPivot,
        web_search: chatOptions.webSearch || undefined,
        neighbor_expansion: chatOptions.neighborExpansion || undefined,
        full_doc_context: chatOptions.fullDocContext || undefined,
        use_v2_ioc_graph: chatOptions.useV2IocGraph || undefined,
        ...(chatOptions.agentLoop !== 'default' && { agent: chatOptions.agentLoop === 'on' }),
        ...(chatOptions.contextLayout !== 'default' && { context_layout: chatOptions.contextLayout }),
      };

      // Inject graph-derived context from pinned nodes
      const { seed_entities, scope_cite_keys } = deriveContextFromGraph(accGraphRef.current, subgraphScope);
      if (seed_entities.length > 0) body.seed_entities = seed_entities;
      if (scope_cite_keys.length > 0) body.scope_cite_keys = scope_cite_keys;

      const ctrl = new AbortController();
      abortCtrlRef.current = ctrl;

      let accContent = '';
      let accSources: SourceDoc[] = [];
      let accSteps: PipelineStep[] = [];
      let accStepDetails: StepDetail[] = [];
      let accPlanningTokens = '';
      let accPlanning: PlanningEvent | null = null;
      let accRouting: RoutingEvent | null = null;
      let accGraphEvent: GraphTraversalEvent | null = null;
      let accProfile: ProfileEvent | null = null;
      let accScoreThreshold: ScoreThreshold | null = null;
      let accQueryIocs: DetectedIOC[] | undefined;
      let accResponseIocs: DetectedIOC[] | undefined;
      let accGraphLocal: AccumulatedGraph = accGraphRef.current;
      let accTurnId: string | undefined;
      let errored = false;

      // Track the query index for graph merging (number of user messages so far)
      const queryIndex =
        baselineMessages.filter((m) => m.role === 'user').length;

      const patchAssistant = (patch: Partial<MessageData>) => {
        setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, ...patch } : m)));
      };

      try {
        for await (const ev of streamChat(body, ctrl.signal)) {
          if (ev.type === 'done') break;
          switch (ev.type) {
            case 'turn_id': {
              accTurnId = ev.turn_id;
              patchAssistant({ turnId: ev.turn_id });
              if (startSessionId) {
                ctx.patchSession(startSessionId, { serverChatId: ev.chat_id });
              }
              break;
            }
            case 'token': {
              accContent += ev.content;
              patchAssistant({ content: accContent });
              break;
            }
            case 'sources': {
              accSources = ev.sources;
              patchAssistant({ sources: ev.sources });
              if (ev.min_score != null || ev.cut_score != null) {
                accScoreThreshold = {
                  min_score: ev.min_score ?? null,
                  cut_score: ev.cut_score ?? null,
                  cutoff_dropped: ev.cutoff_dropped ?? 0,
                  excluded_sources: ev.excluded_sources ?? [],
                };
                patchAssistant({ scoreThreshold: accScoreThreshold });
              }
              break;
            }
            case 'progress': {
              if (!accSteps.includes(ev.step)) accSteps = [...accSteps, ev.step];
              const next = {
                step: ev.step,
                message: ev.message,
                count: ev.count,
                ts: ev.ts ?? Date.now() / 1000,
              };
              // Heartbeats rewrite their step's line; strategy_done is one
              // event per strategy / agent tool call, so every one is kept.
              const existingIdx = (ev.step as string) === 'strategy_done'
                ? -1
                : accStepDetails.findIndex((d) => d.step === ev.step);
              accStepDetails =
                existingIdx >= 0
                  ? accStepDetails.map((d, i) => (i === existingIdx ? next : d))
                  : [...accStepDetails, next];
              patchAssistant({
                progressStep: ev.step,
                progressMessage: ev.message,
                progressCount: ev.count,
                completedSteps: accSteps,
                stepDetails: accStepDetails,
              });
              break;
            }
            case 'planning_token': {
              accPlanningTokens += ev.content;
              patchAssistant({ planningTokens: accPlanningTokens });
              break;
            }
            case 'planning': {
              const { type: _pt, ...planning } = ev;
              void _pt;
              accPlanning = planning as unknown as PlanningEvent;
              accPlanningTokens = ''; // Clear tokens once full plan arrives
              patchAssistant({ planningEvent: accPlanning, planningTokens: undefined });
              break;
            }
            case 'routing': {
              accRouting = {
                mode: ev.mode,
                confidence: ev.confidence,
                reasoning: ev.reasoning,
              };
              patchAssistant({
                routingEvent: accRouting,
                progressMessage: `Routing \u2192 ${ev.mode}`,
              });
              break;
            }
            case 'graph_traversal': {
              const { type: _gt, ...graph } = ev;
              void _gt;
              accGraphEvent = graph as unknown as GraphTraversalEvent;
              if (import.meta.env.DEV) {
                console.debug('[graph_traversal]', {
                  nodes: accGraphEvent.nodes?.length ?? 0,
                  edges: accGraphEvent.edges?.length ?? 0,
                });
              }
              patchAssistant({ graphTraversalEvent: accGraphEvent });
              // Merge into accumulated investigation graph. Track in a local
              // variable so end-of-stream persistence reads it synchronously.
              const prev = accGraphLocal;
              accGraphLocal = mergeGraphEvent(prev, accGraphEvent!, queryIndex, userMsg.id, Date.now());
              setAccGraph(accGraphLocal);
              if (accGraphLocal.nodes.length > prev.nodes.length) {
                setHasUnseenGraphData(true);
              }
              break;
            }
            case 'profile': {
              const { type: _pft, ...profile } = ev;
              void _pft;
              accProfile = profile as unknown as ProfileEvent;
              patchAssistant({ profileEvent: accProfile });
              break;
            }
            case 'detected_iocs': {
              if (ev.source === 'query') {
                accQueryIocs = ev.iocs;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === userMsg.id ? { ...m, queryDetectedIocs: ev.iocs } : m,
                  ),
                );
                // Also thread onto the assistant message so the retrieval card
                // (which renders for the assistant turn) can tie an IOC pivot
                // back to the indicator that triggered it.
                patchAssistant({ queryDetectedIocs: ev.iocs });
              } else {
                accResponseIocs = ev.iocs;
                patchAssistant({ responseDetectedIocs: ev.iocs });
              }
              break;
            }
            case 'error': {
              errored = true;
              toast.error(ev.content);
              patchAssistant({
                content: accContent || `Error: ${ev.content}`,
                streaming: false,
                progressStep: null,
                progressMessage: undefined,
                planningTokens: undefined,
              });
              break;
            }
          }
        }
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') {
          // user-initiated stop
        } else {
          errored = true;
          const errMsg = err instanceof Error ? err.message : String(err);
          toast.error(errMsg);
          patchAssistant({
            content: accContent || `Error: ${errMsg}`,
            streaming: false,
            progressStep: null,
            progressMessage: undefined,
            planningTokens: undefined,
          });
        }
      }

      if (!errored) {
        const finalSteps: PipelineStep[] = accSteps.includes('generating')
          ? accSteps
          : [...accSteps, 'generating' as PipelineStep];

        const finalAssistant: Partial<MessageData> = {
          content: accContent,
          sources: accSources.length > 0 ? accSources : undefined,
          streaming: false,
          progressStep: null,
          progressMessage: undefined,
          planningTokens: undefined,
          completedSteps: finalSteps,
          stepDetails: accStepDetails,
          planningEvent: accPlanning,
          routingEvent: accRouting,
          graphTraversalEvent: accGraphEvent,
          profileEvent: accProfile,
          scoreThreshold: accScoreThreshold,
          responseDetectedIocs: accResponseIocs,
          turnId: accTurnId,
        };
        void accQueryIocs;
        patchAssistant(finalAssistant);

        if (import.meta.env.DEV) {
          console.debug('[ChatApp] streaming ended', {
            completedSteps: finalSteps.length,
            hasRouting: !!accRouting,
            routingMode: accRouting?.mode,
            hasPlanning: !!accPlanning,
            stepDetails: accStepDetails.length,
          });
        }

        // Persist completed messages to session context. accGraphLocal is
        // closed over in this async function and is always synchronously
        // up-to-date — no functional-updater queuing required.
        setMessages((currentMessages) => {
          const patched = currentMessages.map((m) =>
            m.id === assistantId ? { ...m, ...finalAssistant } : m,
          );
          const stored = messageDataToStored(patched);

          if (startSessionId) {
            ctx.updateSessionMessagesById(startSessionId, stored, accGraphLocal);
            // Server just persisted this turn (events, sources, accumulated
            // graph). Invalidate the chat detail so the next read returns
            // server-truth, and the list so updated_at/title refresh.
            ctx.notifyTurnComplete(startSessionId);
          }
          if (startSessionId && !startSessionTitle) {
            const title = deriveTitle(stored);
            if (title) ctx.renameSession(startSessionId, title);
          }
          return patched;
        });
      } else {
        // For an errored stream, make sure we leave the streaming flag cleared
        patchAssistant({
          streaming: false,
          progressStep: null,
          progressMessage: undefined,
          planningTokens: undefined,
        });
      }

      abortCtrlRef.current = null;
      setStreaming(false);
    },
    [streaming, useRag, chatOptions, ctx, activeSession, activeSessionId, subgraphScope],
  );

  const handleStop = useCallback(() => {
    abortCtrlRef.current?.abort();
  }, []);

  const handleSuggestionClick = useCallback((query: string) => {
    chatInputRef.current?.setValue(query);
  }, []);

  // ── Graph management callbacks (same pattern as GraphPanel) ──────────

  const persistGraph = useCallback(
    (next: AccumulatedGraph) => {
      setAccGraph(next);
      if (activeSessionId) {
        const stored = messageDataToStored(messagesRef.current);
        ctx.updateSessionMessagesById(activeSessionId, stored, next);
      }
    },
    [activeSessionId, ctx],
  );

  const handleTogglePin = useCallback(
    (nodeId: string) => {
      const next: AccumulatedGraph = {
        ...accGraph,
        nodes: accGraph.nodes.map(n =>
          n.id === nodeId ? { ...n, pinned: !n.pinned } : n,
        ),
        lastUpdated: Date.now(),
      };
      persistGraph(next);
    },
    [accGraph, persistGraph],
  );

  const handleClearUnpinned = useCallback(() => {
    persistGraph(clearUnpinned(accGraph));
  }, [accGraph, persistGraph]);

  const handleClearAll = useCallback(() => {
    persistGraph(emptyAccumulatedGraph());
  }, [persistGraph]);

  const handleNeighborExpansion = useCallback(
    (centerNodeId: string, nodes: any[], edges: any[]) => {
      const next = mergeNeighborExpansion(accGraph, centerNodeId, nodes, edges);
      persistGraph(next);
    },
    [accGraph, persistGraph],
  );

  const handleNodePositionChange = useCallback(
    (nodeId: string, pos: { x: number; y: number }) => {
      const next: AccumulatedGraph = {
        ...accGraph,
        nodes: accGraph.nodes.map(n =>
          n.id === nodeId ? { ...n, position: pos } : n,
        ),
        lastUpdated: Date.now(),
      };
      persistGraph(next);
    },
    [accGraph, persistGraph],
  );

  const pinnedNodeCount = useMemo(
    () => accGraph.nodes.filter(n => n.pinned).length,
    [accGraph],
  );

  const handleSaveToGraphs = useCallback(() => {
    if (accGraph.nodes.length === 0) {
      toast.error('Graph is empty — nothing to save');
      return;
    }
    const sessionTitle = activeSession?.title || 'Investigation';
    const stamp = new Date().toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    const id = saveGraph(accGraph, `${sessionTitle} \u2014 ${stamp}`);
    toast.success(
      `Saved (${accGraph.nodes.length} nodes)`,
      {
        action: {
          label: 'Open',
          onClick: () => navigate(`/graph?savedGraphId=${encodeURIComponent(id)}`),
        },
      },
    );
  }, [accGraph, activeSession, saveGraph, navigate]);

  // Esc closes fullscreen overlay
  useEffect(() => {
    if (!graphFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setGraphFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [graphFullscreen]);

  const handleToggleGraph = useCallback(() => {
    setGraphFullscreen((open) => {
      const next = !open;
      if (next) setHasUnseenGraphData(false);
      return next;
    });
  }, []);

  const fullscreenOverlay = graphFullscreen
    ? ReactDOM.createPortal(
        <div className="malpedia-graph-fullscreen-overlay" role="dialog" aria-modal="true">
          <div className="malpedia-graph-fullscreen-header">
            <span className="malpedia-graph-fullscreen-title">Investigation Subgraph</span>
            <button
              className="malpedia-graph-fullscreen-close"
              onClick={() => setGraphFullscreen(false)}
              title="Close (Esc)"
              aria-label="Close fullscreen"
            >
              {'\u00D7'}
            </button>
          </div>
          <div className="malpedia-graph-fullscreen-body">
            <AccumulatedSubgraphPanel
              graph={accGraph}
              onTogglePin={handleTogglePin}
              onClearUnpinned={handleClearUnpinned}
              onClearAll={handleClearAll}
              onNeighborExpansion={handleNeighborExpansion}
              onNodePositionChange={handleNodePositionChange}
              subgraphScope={subgraphScope}
              onSetScope={setSubgraphScope}
              messages={messages as any}
              emptyMessage="Graph will populate as you chat."
              onSaveToGraphs={handleSaveToGraphs}
            />
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="malpedia-chat-container">
      <div className="malpedia-chat-main">
        <ChatTopBar
          onNewChat={ctx.createSession}
          onNewTempChat={ctx.createTemporarySession}
          onExport={() => ctx.openExportDialog()}
          isTemporary={activeSession?.temporary}
          isSensitive={activeSession?.serverIsSensitive}
        />
        <MessageList messages={messages} onSuggestionClick={handleSuggestionClick} stats={stats} />
        <ChatInput
          ref={chatInputRef}
          onSend={handleSend}
          disabled={streaming || (llmStatus !== undefined && !llmOk)}
          streaming={streaming}
          llmStatus={llmStatus}
          onStop={handleStop}
          useRag={useRag}
          onToggleRag={() => setUseRag((v) => !v)}
          chatOptions={chatOptions}
          onChatOptionsChange={handleChatOptionsChange}
          estimatedTokens={estimatedTokens}
          maxContextTokens={chatOptions.maxContextTokens}
          graphOpen={graphFullscreen}
          onToggleGraph={handleToggleGraph}
          hasUnseenGraphData={hasUnseenGraphData}
          subgraphScope={subgraphScope}
          pinnedNodeCount={pinnedNodeCount}
        />
      </div>
      {fullscreenOverlay}
    </div>
  );
}
